// NotificationsMenu — the header bell and its dropdown feed. The list is
// fetched LAZILY, on each panel open and never on mount (user requirement: no
// request without opening the popup). The unread COUNT does not come from
// the list: every API response carries the server total in its
// `custom-notifications` header, which axiosBaseQuery mirrors into
// notificationsSlice — so the badge renders before the panel is ever opened
// and reflects the whole feed, not one page.
//
// Rows render per module/status where the enhanced treatment has shipped
// (export, import): an icon tile — icon by module, tone by status — plus a
// two-line title/message body. Modules without an entry in MODULE_ICONS
// (inspections, submissions) keep the plain single text line until their own
// pass. Export-completed rows carry the one row action, Download — a brand
// (primary) button on its own line under the text: resolve the details'
// exportId via export-manager/:id/download-url, then save the file. Download
// is independent of read state and does not mark the row read.
//
// The badge (capped `9+`) and the "N new" chip both show that header count.
// Reads: clicking an UNREAD row marks that one read, "Mark all read" clears
// the page — read rows' main area stays non-interactive (no inert
// affordances). Both mutations are optimistic in notificationsApi (rows and
// count); they undo their patch on failure and this component owns the error
// toast.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Chip, Dropdown, Icon } from '@/components/common';
import { useAppSelector } from '@/store/hooks';
import {
  FIRST_PAGE,
  NotificationModule,
  NotificationStatus,
  useLazyGetMyNotificationsQuery,
  useLazyGetExportDownloadUrlQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
  type AppNotification,
} from '@/api/endpoints/notificationsApi';
import type { ApiErrorShape } from '@/api/types';
import { downloadFileUrl } from '@/utils/downloadUrl';

// Only these modules have their enhanced render implemented; the rest fall
// back to the plain text line.
const MODULE_ICONS: Partial<Record<NotificationModule, string>> = {
  [NotificationModule.IMPORT]: 'upload',
  [NotificationModule.EXPORT]: 'file-down',
};

const STATUS_TONE: Record<NotificationStatus, 'neutral' | 'success' | 'danger'> = {
  [NotificationStatus.STARTED]: 'neutral',
  [NotificationStatus.COMPLETED]: 'success',
  [NotificationStatus.FAILED]: 'danger',
};

/** Row content shared by the unread (button) and read (plain) wrappers. */
const RowContent = ({ notification: n }: { notification: AppNotification }) => {
  const icon = MODULE_ICONS[n.module];
  return (
    <>
      {icon ? (
        <>
          <span
            className={`app-notifications__tile app-notifications__tile--${STATUS_TONE[n.status]}`}
            aria-hidden='true'
          >
            <Icon name={icon} size={16} />
          </span>
          <span className='app-notifications__body'>
            <span className='app-notifications__item-title'>{n.title}</span>
            {n.message && <span className='app-notifications__message'>{n.message}</span>}
          </span>
        </>
      ) : (
        <span className='app-notifications__text'>{n.message ?? n.title}</span>
      )}
      {!n.isRead && <span className='app-notifications__dot' aria-hidden='true' />}
    </>
  );
};

const NotificationsMenu = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [fetchNotifications, { data, isLoading, isError }] = useLazyGetMyNotificationsQuery();
  const [fetchDownloadUrl] = useLazyGetExportDownloadUrlQuery();
  const [readOne] = useReadNotificationMutation();
  const [readAll] = useReadAllNotificationsMutation();
  const unread = useAppSelector((state) => state.notifications.unreadCount);

  const toggle = () => {
    if (!open) fetchNotifications(FIRST_PAGE);
    setOpen(!open);
  };

  const onError = (err: unknown) => toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));

  const markRead = (id: string) => {
    readOne(id).unwrap().catch(onError);
  };

  const markAllRead = () => {
    readAll().unwrap().catch(onError);
  };

  const downloadExport = async (n: AppNotification) => {
    if (!n.exportDetails || downloadingId) return;
    setDownloadingId(n.id);
    try {
      const url = await fetchDownloadUrl(n.exportDetails.exportId).unwrap();
      await downloadFileUrl(url, n.exportDetails.fileName ?? 'export.csv');
    } catch (err) {
      onError(err);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      width={360}
      trigger={
        <button
          type='button'
          className='app-header__icon-btn app-header__bell'
          aria-label={t('header_notifications')}
          title={t('header_notifications')}
          aria-haspopup='true'
          aria-expanded={open}
          onClick={toggle}
        >
          <Icon name='bell' size={18} />
          {unread > 0 && <span className='app-header__badge'>{unread > 9 ? '9+' : unread}</span>}
        </button>
      }
    >
      {/* Single wrapper that pulls itself over the panel's padding so the
          head border, row separators and unread tints run edge-to-edge. */}
      <div className='app-notifications'>
        <div className='app-notifications__head'>
          <span className='app-notifications__title'>{t('header_notifications')}</span>
          {unread > 0 && (
            <>
              <Chip variant='brand'>{t('notifications_new_count', { count: unread })}</Chip>
              <button type='button' className='app-notifications__mark-all' onClick={markAllRead}>
                {t('notifications_mark_all_read')}
              </button>
            </>
          )}
        </div>

        {isLoading ? (
          <div className='app-notifications__state'>{t('text_loading')}</div>
        ) : isError ? (
          <div className='app-notifications__state'>
            <Icon name='circle-alert' size={22} />
            <span>{t('notifications_error')}</span>
            <Button variant='ghost' size='sm' onClick={() => fetchNotifications(FIRST_PAGE)}>
              {t('notifications_retry')}
            </Button>
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className='app-notifications__state'>
            <Icon name='bell' size={22} />
            <span>{t('notifications_empty')}</span>
          </div>
        ) : (
          <ul className='app-notifications__list'>
            {data.items.map((n) => (
              <li
                key={n.id}
                className={`app-notifications__row${n.isRead ? '' : ' app-notifications__row--unread'}`}
              >
                {n.isRead ? (
                  <div className='app-notifications__row-main'>
                    <RowContent notification={n} />
                  </div>
                ) : (
                  <button
                    type='button'
                    className='app-notifications__row-btn'
                    onClick={() => markRead(n.id)}
                  >
                    <RowContent notification={n} />
                  </button>
                )}
                {n.status === NotificationStatus.COMPLETED && n.exportDetails && (
                  <div className='app-notifications__actions'>
                    <Button
                      variant='primary'
                      size='sm'
                      iconStart='download'
                      loading={downloadingId === n.id}
                      onClick={() => downloadExport(n)}
                    >
                      {t('notifications_download')}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dropdown>
  );
};

export default NotificationsMenu;
